import React, { useState, useEffect } from 'react';
import WebsiteChatSection from '../Chatbot/WebsiteChatSection';
import { PAGE, SettingsHeader } from './SettingsUI';
import {
  Bot, Phone, Sparkles, Save, Eye, EyeOff, Copy, Check, Loader2, Key, Info, BookOpen,
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { useModules } from '../../context/FeatureContext';

/* ── Toggle ─────────────────────────────────────────────────────────────── */
function Toggle({ checked, onChange, disabled }) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      className={clsx(
        'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none',
        checked ? 'bg-primary-600' : 'bg-slate-200',
        disabled && 'opacity-50 cursor-not-allowed'
      )}
    >
      <span className={clsx(
        'inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform',
        checked ? 'translate-x-6' : 'translate-x-1'
      )} />
    </button>
  );
}

/* ── Section card ───────────────────────────────────────────────────────── */
function SectionCard({ title, icon: Icon, children, subtitle }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 max-w-2xl">
      <div className="flex items-center gap-2 pb-3 border-b border-slate-100 mb-3">
        {Icon && <Icon size={16} className="text-primary-600" />}
        <div>
          <h3 className="text-sm font-bold text-slate-900">{title}</h3>
          {subtitle && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

/* ── Plain text/select field ────────────────────────────────────────────── */
function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700">{label}</label>
      {hint && <p className="text-xs text-slate-400 mt-0.5 mb-1.5">{hint}</p>}
      <div className={hint ? '' : 'mt-1.5'}>{children}</div>
    </div>
  );
}

const inputCls =
  'w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary-400 disabled:bg-slate-50 disabled:text-slate-400';

/* ── Secret input (masked placeholder + show/hide) ──────────────────────── */
function SecretField({ label, hint, hasValue, last4, value, onChange, placeholderName }) {
  const [show, setShow] = useState(false);
  const masked = hasValue
    ? `••••${last4 || ''} — leave blank to keep`
    : `Enter ${placeholderName || label}`;
  return (
    <Field label={label} hint={hint}>
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={masked}
          autoComplete="off"
          className={clsx(inputCls, 'pr-10')}
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600"
          tabIndex={-1}
          aria-label={show ? 'Hide value' : 'Show value'}
        >
          {show ? <EyeOff size={15} /> : <Eye size={15} />}
        </button>
      </div>
    </Field>
  );
}

/* ── Page ───────────────────────────────────────────────────────────────── */
const NON_SECRET_KEYS = [
  'enabled',
  'voice_mode',
  'twilio_phone_number',
  'twilio_transfer_number',
  'elevenlabs_voice_id',
];

// Knowledge / "training" fields — what the receptionist knows about your business.
// These drive what it says to callers.
const KNOWLEDGE_KEYS = [
  'company_name',
  'welcome_message',
  'services',
  'pricing_info',
  'operating_hours',
  'service_areas',
  'languages_spoken',
  'intake_process',
  'referral_process',
  'additional_info',
];

// Secret field key → matching has_* / *_last4 hints from the GET response
const SECRET_KEYS = [
  'twilio_account_sid',
  'twilio_auth_token',
  'elevenlabs_api_key',
  'groq_api_key',
  'deepgram_api_key',
  'anthropic_api_key',
];

export default function ReceptionistSettings() {
  const modules = useModules();
  const enabledModule = modules.ai_receptionist === true;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  // Masked config returned from the server (booleans, last4 hints, plain values)
  const [config, setConfig] = useState(null);

  // Non-secret editable form state
  const [form, setForm] = useState({
    enabled: false,
    voice_mode: 'gather',
    twilio_phone_number: '',
    twilio_transfer_number: '',
    elevenlabs_voice_id: '',
    // knowledge / training
    company_name: '',
    welcome_message: '',
    services: '',
    pricing_info: '',
    operating_hours: '',
    service_areas: '',
    languages_spoken: '',
    intake_process: '',
    referral_process: '',
    additional_info: '',
  });

  // Secret inputs — only ever hold what the user typed (never the real value)
  const [secrets, setSecrets] = useState({
    twilio_account_sid: '',
    twilio_auth_token: '',
    elevenlabs_api_key: '',
    groq_api_key: '',
    deepgram_api_key: '',
    anthropic_api_key: '',
  });

  const loadConfig = () => {
    return api
      .get('/receptionist/my-config')
      .then((r) => {
        const d = r.data || {};
        setConfig(d);
        setForm({
          enabled: !!d.enabled,
          voice_mode: d.voice_mode || 'gather',
          twilio_phone_number: d.twilio_phone_number || '',
          twilio_transfer_number: d.twilio_transfer_number || '',
          elevenlabs_voice_id: d.elevenlabs_voice_id || '',
          ...KNOWLEDGE_KEYS.reduce((o, k) => ({ ...o, [k]: d[k] || '' }), {}),
        });
        // Clear typed secrets after a (re)load so masked state shows again
        setSecrets({
          twilio_account_sid: '',
          twilio_auth_token: '',
          elevenlabs_api_key: '',
          groq_api_key: '',
          deepgram_api_key: '',
          anthropic_api_key: '',
        });
      });
  };

  useEffect(() => {
    if (!enabledModule) {
      setLoading(false);
      return;
    }
    loadConfig()
      .catch(() => toast.error('Failed to load AI Receptionist config'))
      .finally(() => setLoading(false));
  }, [enabledModule]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSecret = (k, v) => setSecrets((s) => ({ ...s, [k]: v }));

  const copyWebhook = async () => {
    const url = config?.webhook_url;
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Could not copy to clipboard');
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = {};
      // Always send non-secret + knowledge fields (empty = explicitly cleared)
      [...NON_SECRET_KEYS, ...KNOWLEDGE_KEYS].forEach((k) => { body[k] = form[k]; });
      // Only send secrets the user actually typed (empty = leave unchanged)
      SECRET_KEYS.forEach((k) => {
        if (secrets[k] && secrets[k].trim() !== '') body[k] = secrets[k].trim();
      });

      const r = await api.put('/receptionist/my-config', body);
      toast.success(r.data?.message || 'AI Receptionist settings saved');
      await loadConfig(); // reload masked config (refreshes last4 / has_* hints)
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  /* ── Module not granted ─────────────────────────────────────────────── */
  if (!enabledModule) {
    return (
      <div className={PAGE}>
        <SettingsHeader title="AI receptionist" />
        <div className="bg-white rounded-2xl border border-slate-200 p-8 max-w-2xl text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-3">
            <Bot size={22} className="text-slate-400" />
          </div>
          <h3 className="text-sm font-bold text-slate-900">AI Receptionist isn't enabled for your organisation</h3>
          <p className="text-sm text-slate-500 mt-1">
            This feature is granted per organisation. Please contact your administrator to enable it.
          </p>
        </div>
        {/* Website chat is its own module, so an org without the receptionist can still switch it on. */}
        <WebsiteChatSection hideWhenLocked className="max-w-2xl" />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className={PAGE}>
      <SettingsHeader
        title="AI receptionist"
        description="The AI phone receptionist that answers calls for your organisation."
        actions={
          <button onClick={save} disabled={saving} className="btn-primary">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Changes
          </button>
        }
      />

      {/* Enable toggle */}
      <SectionCard title="Status" icon={Bot}>
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-slate-700">Enable AI Receptionist</p>
            <p className="text-xs text-slate-400 mt-0.5">
              When on, incoming calls to your Twilio number are answered by the AI receptionist.
            </p>
          </div>
          <Toggle checked={form.enabled} onChange={(v) => setField('enabled', v)} />
        </div>
      </SectionCard>

      {/* How it works + webhook */}
      <SectionCard title="How it works" icon={Info}>
        <p className="text-sm text-slate-600">
          The AI receptionist answers calls placed to your Twilio phone number. Point your Twilio
          number's <span className="font-medium text-slate-800">Voice webhook</span> to the URL below,
          then enter your provider keys in the sections that follow.
        </p>
        <Field
          label="Webhook URL"
          hint="In the Twilio console, set your phone number's “A call comes in” webhook (HTTP POST) to this URL."
        >
          <div className="flex items-stretch gap-2">
            <input
              type="text"
              readOnly
              value={config?.webhook_url || 'Available after saving your configuration'}
              onFocus={(e) => e.target.select()}
              className={clsx(inputCls, 'font-mono text-xs bg-slate-50 text-slate-600 cursor-text')}
            />
            <button
              type="button"
              onClick={copyWebhook}
              disabled={!config?.webhook_url}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-50 shrink-0"
            >
              {copied ? <Check size={13} className="text-green-600" /> : <Copy size={13} />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </Field>
      </SectionCard>

      {/* Knowledge / Training */}
      <SectionCard
        title="Knowledge & Training"
        icon={BookOpen}
        subtitle="Teach the receptionist about your organisation — this is what it tells callers."
      >
        <Field label="Business / Service Name" hint="How the receptionist refers to your organisation.">
          <input type="text" value={form.company_name} onChange={(e) => setField('company_name', e.target.value)}
            placeholder="e.g. Demo Care Co" className={inputCls} />
        </Field>
        <Field label="Greeting" hint="The opening line callers hear (leave blank for a friendly default).">
          <textarea rows={2} value={form.welcome_message} onChange={(e) => setField('welcome_message', e.target.value)}
            placeholder="e.g. Thanks for calling Demo Care Co, how can I help?" className={inputCls} />
        </Field>
        <Field label="Services you provide" hint="What the receptionist can tell callers you offer.">
          <textarea rows={4} value={form.services} onChange={(e) => setField('services', e.target.value)}
            placeholder={"e.g.\n- Daily personal care & community access\n- Supported Independent Living (SIL)\n- Support coordination"} className={inputCls} />
        </Field>
        <Field label="Pricing information" hint="How to answer pricing questions (kept general).">
          <textarea rows={2} value={form.pricing_info} onChange={(e) => setField('pricing_info', e.target.value)}
            placeholder="e.g. We follow NDIS Price Guide rates; the team can send a quote." className={inputCls} />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Operating hours">
            <input type="text" value={form.operating_hours} onChange={(e) => setField('operating_hours', e.target.value)}
              placeholder="Mon–Fri 9am–5pm AEST" className={inputCls} />
          </Field>
          <Field label="Areas served">
            <input type="text" value={form.service_areas} onChange={(e) => setField('service_areas', e.target.value)}
              placeholder="Greater Sydney, Western Sydney" className={inputCls} />
          </Field>
          <Field label="Languages">
            <input type="text" value={form.languages_spoken} onChange={(e) => setField('languages_spoken', e.target.value)}
              placeholder="English, Arabic" className={inputCls} />
          </Field>
        </div>
        <Field label="Intake / referral process" hint="What to tell new participants or referrers about getting started.">
          <textarea rows={3} value={form.intake_process} onChange={(e) => setField('intake_process', e.target.value)}
            placeholder="e.g. Take their details and a coordinator calls back within 1 business day." className={inputCls} />
        </Field>
        <Field label="Anything else the receptionist should know" hint="Free-form notes, FAQs, policies — the AI uses this when relevant.">
          <textarea rows={4} value={form.additional_info} onChange={(e) => setField('additional_info', e.target.value)}
            placeholder="e.g. We don't currently take SDA referrals. For complaints, transfer to the office." className={inputCls} />
        </Field>
      </SectionCard>

      <WebsiteChatSection hideWhenLocked className="max-w-2xl" />

      {/* Telephony */}
      <SectionCard
        title="Telephony"
        icon={Phone}
        subtitle="Twilio credentials and the numbers callers reach."
      >
        <SecretField
          label="Twilio Account SID"
          placeholderName="Account SID"
          hasValue={config?.has_twilio_account_sid}
          last4={config?.twilio_account_sid_last4}
          value={secrets.twilio_account_sid}
          onChange={(v) => setSecret('twilio_account_sid', v)}
        />
        <SecretField
          label="Twilio Auth Token"
          placeholderName="Auth Token"
          hasValue={config?.has_twilio_auth_token}
          last4={config?.twilio_auth_token_last4}
          value={secrets.twilio_auth_token}
          onChange={(v) => setSecret('twilio_auth_token', v)}
        />
        <Field label="Twilio Phone Number" hint="The number callers dial to reach your receptionist.">
          <input
            type="text"
            value={form.twilio_phone_number}
            onChange={(e) => setField('twilio_phone_number', e.target.value)}
            placeholder="+61 2 1234 5678"
            className={inputCls}
          />
        </Field>
        <Field label="Transfer Number" hint="Calls are transferred here when the AI hands off to a human.">
          <input
            type="text"
            value={form.twilio_transfer_number}
            onChange={(e) => setField('twilio_transfer_number', e.target.value)}
            placeholder="+61 4 0000 0000"
            className={inputCls}
          />
        </Field>
      </SectionCard>

      {/* Voice (AI) */}
      <SectionCard
        title="Voice (AI)"
        icon={Sparkles}
        subtitle="Speech, language and reasoning provider keys."
      >
        <SecretField
          label="ElevenLabs API Key"
          placeholderName="ElevenLabs API Key"
          hasValue={config?.has_elevenlabs_api_key}
          last4={config?.elevenlabs_api_key_last4}
          value={secrets.elevenlabs_api_key}
          onChange={(v) => setSecret('elevenlabs_api_key', v)}
        />
        <Field label="ElevenLabs Voice ID" hint="The voice used for the receptionist's speech.">
          <input
            type="text"
            value={form.elevenlabs_voice_id}
            onChange={(e) => setField('elevenlabs_voice_id', e.target.value)}
            placeholder="e.g. 21m00Tcm4TlvDq8ikWAM"
            className={inputCls}
          />
        </Field>
        <SecretField
          label="Groq API Key"
          placeholderName="Groq API Key"
          hasValue={config?.has_groq_api_key}
          last4={config?.groq_api_key_last4}
          value={secrets.groq_api_key}
          onChange={(v) => setSecret('groq_api_key', v)}
        />
        <SecretField
          label="Deepgram API Key"
          placeholderName="Deepgram API Key"
          hasValue={config?.has_deepgram_api_key}
          last4={config?.deepgram_api_key_last4}
          value={secrets.deepgram_api_key}
          onChange={(v) => setSecret('deepgram_api_key', v)}
        />
        <SecretField
          label="Anthropic API Key"
          placeholderName="Anthropic API Key"
          hasValue={config?.has_anthropic_api_key}
          last4={config?.anthropic_api_key_last4}
          value={secrets.anthropic_api_key}
          onChange={(v) => setSecret('anthropic_api_key', v)}
        />
        <Field label="Voice Mode" hint="“Gather” uses turn-based prompts; “Stream” uses real-time streaming audio.">
          <select
            value={form.voice_mode}
            onChange={(e) => setField('voice_mode', e.target.value)}
            className={inputCls}
          >
            <option value="gather">Gather (turn-based)</option>
            <option value="stream">Stream (real-time)</option>
          </select>
        </Field>
      </SectionCard>

      {/* Footer save (mirrors header) */}
      <div className="max-w-2xl flex justify-end">
        <button
          onClick={save}
          disabled={saving}
          className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors disabled:opacity-60"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Changes
        </button>
      </div>
    </div>
  );
}
